import { Camera } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { Product } from "@kira/contracts";

// Local visual retrieval: spatial RGB signature plus color distribution.
// Images stay in the browser; no semantic recognition or external model is claimed.
async function signature(url: string): Promise<number[]> {
  const img = new Image();
  img.crossOrigin = "anonymous";
  await new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(
      () => reject(new Error("图片读取超时")),
      10000,
    );
    img.onload = () => {
      clearTimeout(timer);
      resolve();
    };
    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error("图片无法读取"));
    };
    img.src = url;
  });
  const canvas = document.createElement("canvas");
  canvas.width = 16;
  canvas.height = 16;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw Error("浏览器不支持图片检索");
  ctx.drawImage(img, 0, 0, 16, 16);
  const pixels = ctx.getImageData(0, 0, 16, 16).data;
  const values: number[] = [];
  const histogram = Array<number>(24).fill(0);
  for (let i = 0; i < pixels.length; i += 4)
    for (let c = 0; c < 3; c++) {
      const value = pixels[i + c]! / 255;
      values.push(value);
      histogram[c * 8 + Math.min(7, Math.floor(value * 8))]! += 1 / 256;
    }
  return [...values.map((v) => v / Math.sqrt(768)), ...histogram];
}
export function ImageProductSearch({
  products,
  onResults,
}: {
  products: Product[];
  onResults: (ids: string[] | null) => void;
}) {
  const [preview, setPreview] = useState(""),
    [busy, setBusy] = useState(false),
    [status, setStatus] = useState("");
  const input = useRef<HTMLInputElement>(null),
    version = useRef(0);
  const cache = useRef(new Map<string, Promise<number[] | null>>());
  useEffect(
    () => () => {
      version.current++;
    },
    [],
  );
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview],
  );
  async function search(file: File) {
    const v = ++version.current;
    if (!file.type.startsWith("image/") || file.size > 8 * 1024 * 1024) {
      setStatus("请选择 8MB 以内的图片");
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    setBusy(true);
    setStatus("正在比较商品图片…");
    onResults(null);
    try {
      const query = await signature(url);
      const scores: { id: string; score: number }[] = [];
      const candidates = products.filter(
        (p) => p.status === "active" && p.imageUrl,
      );
      for (let i = 0; i < candidates.length; i += 4) {
        if (v !== version.current) return;
        await Promise.all(
          candidates.slice(i, i + 4).map(async (p) => {
            let cached = cache.current.get(p.imageUrl);
            if (!cached) {
              cached = signature(p.imageUrl).catch(() => null);
              cache.current.set(p.imageUrl, cached);
            }
            const value = await cached;
            if (value)
              scores.push({
                id: p.id,
                score: value.reduce(
                  (sum, n, index) => sum + (n - query[index]!) ** 2,
                  0,
                ),
              });
          }),
        );
      }
      if (v !== version.current) return;
      if (!scores.length)
        throw Error("商品图片暂时无法读取，请重试或使用文字搜索");
      scores.sort((a, b) => a.score - b.score);
      onResults(scores.slice(0, 12).map((s) => s.id));
      setStatus(
        `已比较 ${scores.length} 件商品，按颜色与轮廓相似度排序${scores.length < candidates.length ? "；部分图片无法读取" : ""}`,
      );
    } catch (e) {
      if (v === version.current)
        setStatus(e instanceof Error ? e.message : "图片搜索失败");
    } finally {
      if (v === version.current) setBusy(false);
    }
  }
  return (
    <div className="market-image-search">
      <input
        ref={input}
        type="file"
        accept="image/*"
        aria-label="选择图片搜商品"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void search(file);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        disabled={busy}
        onClick={() => input.current?.click()}
      >
        <Camera size={19}/>{busy ? "比对中…" : "以图搜物"}
      </button>
      {preview ? (
        <div className="market-image-search__result">
          <img src={preview} alt="搜索参考图" />
          <p role="status">{status}</p>
          <button
            type="button"
            onClick={() => {
              version.current++;
              setPreview("");
              setStatus("");
              setBusy(false);
              onResults(null);
            }}
          >
            清除图片
          </button>
        </div>
      ) : status ? (
        <p role="alert">{status}</p>
      ) : null}
    </div>
  );
}
